import React, { useEffect } from 'react';
import {
  AddUniversal,
  ShowOutlined,
  TrashCanOutlined,
} from '@duxton-web/icons';
import {
  Button,
  Input,
  InputNumber,
  Popover,
  Space,
  Typography,
} from '@duxton-web/ui';
import { produce } from 'immer';
import styled from 'styled-components';

import { DataType } from '@/common/components';
import {
  useVariableContext,
  useVariableDispatchContext,
} from '@/features/variable/contexts/variable-context';
import {
  getSchemaTypeByVariableType,
  isEmpty,
  JSONSchema,
  SchemaType,
} from '@/features/variable/settings/validation';
import { variableJsonSchemaUpdate } from '@/features/variable/store/actions/variable/action-creators';
import { VariableInfo } from '@/lib/PlannerDTOs';

import './index.css';

import { isFieldChanged } from '@/features/variable/utils';

export const hasDuplicate = (arr: any[], value: any): boolean => {
  return arr.filter((val) => value === val)?.length > 1;
};

export const hasNoValidValues = (_arr: any[], _variableType?) => {
  return false;
};

const getVariableSchema = (variable: VariableInfo): JSONSchema => {
  if (!variable?.metadata?.jsonSchema)
    return {
      type: getSchemaTypeByVariableType(variable) as SchemaType,
      enum: [],
    };

  return JSON.parse(variable.metadata.jsonSchema);
};

const getValues = (variable: VariableInfo) => {
  const nextVariable = produce(variable, (_draft) => {});

  return getVariableSchema(nextVariable)?.enum || [];
};

const validateValue = (variable: VariableInfo, value: any): string => {
  const arrToCompare = getValues(variable);

  if (isEmpty(variable, value)) return 'Value cannot be empty';

  if (hasDuplicate(arrToCompare, value)) return 'Duplicate value';

  return '';
};

const validValues = (_variable: VariableInfo, arr: string[] | number[]) => {
  const hasInvalidItem = arr.some((item) => hasDuplicate(arr, item));

  if (hasInvalidItem) return false;

  return true;
};

interface SelectionErrorProps {
  value: any;
}

const SelectionError = ({ value }: SelectionErrorProps) => {
  const { variable } = useVariableContext();

  const invalidValue = validateValue(variable, value);

  if (!invalidValue) return null;

  return <Typography.Text type="danger">{invalidValue}</Typography.Text>;
};

interface SelectionsContentProps {
  value: any;
  index: number;
}

const SelectionContent = ({ value, index }: SelectionsContentProps) => {
  const { variable, disabled, schemaDiff } = useVariableContext();
  const dispatch = useVariableDispatchContext();
  const showDeleteButton = getValues(variable).length > 1;

  const invalidValue = validateValue(variable, value);

  const enumValueChange = (value: any, index: number) => {
    const arrToUpdate = getValues(variable);
    const schema = getVariableSchema(variable);

    arrToUpdate.splice(index, 1, value);

    dispatch(
      variableJsonSchemaUpdate(
        {
          type: schema.type as SchemaType,
          enum: arrToUpdate,
        },
        validValues(variable, arrToUpdate),
      ),
    );
  };

  const deleteSelectionValue = (index: number): void => {
    if (index < 0 || index > getValues(variable).length - 1) return;

    const arrToUpdate = getValues(variable);
    const schema = getVariableSchema(variable);

    arrToUpdate.splice(index, 1);

    dispatch(
      variableJsonSchemaUpdate(
        {
          type: schema.type as SchemaType,
          enum: arrToUpdate,
        },
        validValues(variable, arrToUpdate),
      ),
    );
  };

  switch (variable?.type) {
    case DataType.INT64:
    case DataType.FLOAT64:
      return (
        <Space.Compact>
          <InputNumber
            defaultValue={value}
            disabled={disabled}
            className={`validation-input-number ${
              isFieldChanged(schemaDiff, 'enum')
                ? 'changelog-item-variable-modified'
                : ''
            }`}
            onChange={(e) => enumValueChange(e, index)}
            status={invalidValue ? 'error' : ''}
          />
          {showDeleteButton && (
            <Button
              className="enum-delete-button"
              disabled={disabled}
              danger={invalidValue !== ''}
              onClick={() => deleteSelectionValue(index)}
              shape="default"
              icon={<TrashCanOutlined />}
            />
          )}
        </Space.Compact>
      );
    case DataType.FILE:
      return (
        <Space.Compact className="validation-input">
          <Input
            className={`validation-input-number ${
              isFieldChanged(schemaDiff, 'enum')
                ? 'changelog-item-variable-modified'
                : ''
            }`}
            defaultValue={value}
            disabled={disabled}
            onChange={(e) => enumValueChange(e.target.value, index)}
            status={invalidValue ? 'error' : ''}
          />
          <Popover
            placement="topLeft"
            title={'Preview'}
            content={
              <img
                style={{
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  maxWidth: '400px',
                  display: 'block',
                }}
                className="file_input__preview"
                src={value}
                alt="Please provide a valid image url"
              />
            }
            trigger="click"
          />
          <Button
            danger={invalidValue !== ''}
            style={{
              borderRadius: '0',
            }}
          >
            <ShowOutlined />
          </Button>
          {showDeleteButton && (
            <Button
              className="enum-delete-button"
              disabled={disabled}
              danger={invalidValue !== ''}
              onClick={() => deleteSelectionValue(index)}
              shape="default"
              icon={<TrashCanOutlined />}
            />
          )}
        </Space.Compact>
      );
    default:
      return (
        <Space.Compact className="validation-input">
          <Input
            className={`validation-input-number ${
              isFieldChanged(schemaDiff, 'enum')
                ? 'changelog-item-variable-modified'
                : ''
            }`}
            defaultValue={value}
            disabled={disabled}
            onChange={(e) => enumValueChange(e.target.value, index)}
            status={invalidValue ? 'error' : ''}
          />
          {showDeleteButton && (
            <Button
              className="enum-delete-button"
              disabled={disabled}
              danger={invalidValue !== ''}
              onClick={() => deleteSelectionValue(index)}
              shape="default"
              icon={<TrashCanOutlined />}
            />
          )}
        </Space.Compact>
      );
  }
};

const Enum = () => {
  const { variable, disabled } = useVariableContext();
  const dispatch = useVariableDispatchContext();

  useEffect(() => {
    if (disabled) return;
    if (getValues(variable).length > 0) return;

    const schema = getVariableSchema(variable);
    const initialValue = schema.type === SchemaType.STRING ? '' : 0;
    const nextEnum = [initialValue];

    dispatch(
      variableJsonSchemaUpdate(
        {
          type: schema.type as SchemaType,
          enum: nextEnum,
        } as JSONSchema,
        validValues(variable, nextEnum as any),
      ),
    );
  }, [dispatch, disabled, variable]);

  const handleAddValidValues = () => {
    const arrToUpdate = getValues(variable);
    const schema = getVariableSchema(variable);

    dispatch(
      variableJsonSchemaUpdate(
        {
          type: schema.type as SchemaType,
          enum: arrToUpdate.concat(
            schema.type === SchemaType.STRING ? [''] : [0],
          ),
        } as JSONSchema,
        validValues(variable, arrToUpdate),
      ),
    );
  };

  return (
    <div
      id="enum-selections-container"
      style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
    >
      <Typography.Metadata style={{ color: '#A3A3A3' }}>
        Valid Values
      </Typography.Metadata>
      <ValidValuesContainer>
        {getValues(variable).map((value: any, index: number) => {
          return (
            <div
              data-testid={`enum-selections-${index}`}
              key={`enum-selections-${index}`}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                alignItems: 'flex-start',
              }}
            >
              <SelectionContent value={value} index={index} />
              <SelectionError value={value} />
            </div>
          );
        })}
        {!disabled && (
          <Button
            data-testid="enum-add"
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '12px',
              background: '#FFF',
              border: '1px solid #E8E8E8',
              padding: '0',
            }}
            onClick={handleAddValidValues}
            icon={<AddUniversal />}
          />
        )}
      </ValidValuesContainer>
    </div>
  );
};

const ValidValuesContainer = styled.div`
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 8px;
`;

export default Enum;
